import { yupResolver } from "@hookform/resolvers/yup";
import {
	Box,
	Button,
	IconButton,
	Modal,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { CloseWhiteIcon } from "assests/images";
import { SuccessErrorModalDispatchContext } from "contexts/success-error-context";
import React, { useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import dashboardService from "services/dashboard-service";
import { commonModalStyle, createCommonAPICall } from "utility/helpers";
import { revertCaseReasonSchema } from "utility/validations";

type ComponentProps = {
	isRevertCaseReasonModalOpen: boolean;
	handleRevertCaseReasonModalClose: any;
	getDashboardDetails: any;
	queryString: string;
};

export function RevertCaseReasonModal(props: ComponentProps) {
	// Page level interface
	interface IRevertCaseReason {
		reason: string;
	}

	//Page level local variable
	const setSuccessErrorContext = useContext(SuccessErrorModalDispatchContext);

	// extract props
	const {
		handleRevertCaseReasonModalClose,
		isRevertCaseReasonModalOpen,
		getDashboardDetails,
		queryString,
	} = props;

	// State Variables
	const [formValues, setFormValues] = useState<IRevertCaseReason>({
		reason: "",
	});

	// form validator
	const {
		register,
		handleSubmit,
		formState: { errors },
		reset,
		clearErrors,
		setValue,
	} = useForm({
		resolver: yupResolver(revertCaseReasonSchema),
	});

	// handle events and function
	const handleFormValueChange = (e: any) => {
		setFormValues({ ...formValues, [e.target.name]: e.target.value });
	};

	const handleRevertCaseReasonSubmit = async () => {
		const requestBody = {
			queryString: queryString,
			...formValues,
		};

		const data = await createCommonAPICall({
			requestBody,
			apiService: dashboardService.revertCancelledCaseToNew,
			showErrorMessage: true,
			showSuccessMessage: true,
			setSuccessErrorContext,
		});

		if (data && data.isSuccessfull) {
			getDashboardDetails();
			handleRevertCaseReasonModalClose();
		}
	};

	// use effects
	useEffect(() => {
		reset();
		clearErrors();
		setFormValues({
			reason: "",
		});
	}, [isRevertCaseReasonModalOpen]);

	useEffect(() => {
		Object.entries(formValues).forEach((child) => {
			setValue(child[0] as any, child[1] || "");
		});
	}, [formValues]);

	return (
		<>
			<Modal
				open={isRevertCaseReasonModalOpen}
				onClose={handleRevertCaseReasonModalClose}
				className="send-modal select-input-modal"
			>
				<Box sx={commonModalStyle}>
					<Box className="modal-header">
						<Typography variant="h5">Revert Case to New</Typography>
						<IconButton onClick={handleRevertCaseReasonModalClose}>
							<img src={CloseWhiteIcon} alt="close" />
						</IconButton>
					</Box>
					<Box className="modal-body">
						<div>
							<div>
								<Typography variant="body1" className="instruction">
									Please a provide reason
								</Typography>
							</div>
							<TextField
								{...register("reason")}
								name="reason"
								id="block"
								label="Reason"
								value={formValues.reason}
								onChange={handleFormValueChange}
								multiline
								maxRows={1}
								inputProps={{ className: "min-h-88px" }}
								fullWidth
								error={errors?.reason?.message ? true : false}
								helperText={errors?.reason?.message?.toString()}
							/>
						</div>
						<div className="modal-footer">
							<Tooltip title="Revert">
								<Button
									variant="contained"
									onClick={handleSubmit(handleRevertCaseReasonSubmit)}
								>
									Revert
								</Button>
							</Tooltip>
							<Tooltip title="Cancel">
								<Button
									variant="outlined"
									onClick={handleRevertCaseReasonModalClose}
								>
									Cancel
								</Button>
							</Tooltip>
						</div>
					</Box>
				</Box>
			</Modal>
		</>
	);
}
